import { useTranslation } from "react-i18next";
import { CalendarBlank, CheckCircle, Warning, Check } from "@phosphor-icons/react";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { TNextStep } from "@/zapi-frontend/stores/project";
import { SectionCard } from "./SectionCard";

function parseStep(step: TNextStep) {
  const parts = step.text.split("|").map((p) => p.trim());
  const action = step.summary || parts[0] || step.text;
  const timeframe = parts.find((p) => /day|week|month|quarter|next/i.test(p) && p.length < 40);
  const criteria = parts[parts.length - 1] || "";
  const killIdx = criteria.search(/kill\s*:/i);
  const success = killIdx >= 0 ? criteria.slice(0, killIdx) : criteria;
  const kill = killIdx >= 0 ? criteria.slice(killIdx) : "";
  const clean = (s: string) => s.replace(/^(success|gate|kill)\s*:/i, "").trim();
  return { action, timeframe, success: clean(success), kill: clean(kill) };
}

function StepCard({ step }: { step: TNextStep }) {
  const { t } = useTranslation("projectHome");
  const hasStructured = !!(step.timeframe || step.successCriteria || step.killCriteria);
  const legacy = hasStructured ? null : parseStep(step);
  const action = step.summary || legacy?.action || step.text;
  const timeframe = step.timeframe || legacy?.timeframe || "";
  const success = step.successCriteria
    ? t("overview.gatePrefix", { criteria: step.successCriteria })
    : legacy?.success || "";
  const kill = step.killCriteria || legacy?.kill || "";
  return (
    <div className="flex-1 flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <div
          className={cn(
            "size-4 shrink-0 rounded border flex items-center justify-center",
            step.completed ? "bg-innoveraZinc-900 border-innoveraZinc-900" : "bg-white border-innoveraZinc-200"
          )}
        >
          {step.completed && <Check className="size-3 text-white" weight="bold" />}
        </div>
        <div className="h-px flex-1 bg-innoveraZinc-150" />
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-1">
          {timeframe && (
            <div className="flex items-center gap-2 text-innoveraZinc-600">
              <CalendarBlank className="size-4" weight="regular" />
              <FigmaTypography variant="body-3" color="zinc600">
                {timeframe}
              </FigmaTypography>
            </div>
          )}
          <span className="text-[14px] leading-[22px] font-medium tracking-[-0.14px] text-innoveraZinc-900">
            {action}
          </span>
        </div>
        <div className="flex flex-col gap-1">
          {success && (
            <div className="flex items-start gap-2">
              <CheckCircle className="size-4 mt-0.5 shrink-0 text-innoveraZinc-600" weight="regular" />
              <FigmaTypography variant="body-3" color="zinc600">
                {success}
              </FigmaTypography>
            </div>
          )}
          {kill && (
            <div className="flex items-start gap-2">
              <Warning className="size-4 mt-0.5 shrink-0 text-innoveraZinc-600" weight="regular" />
              <FigmaTypography variant="body-3" color="zinc600">
                {kill}
              </FigmaTypography>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function NextStepsBlock({ nextSteps }: { nextSteps: TNextStep[] }) {
  const { t } = useTranslation("projectHome");
  if (!nextSteps.length) return null;
  return (
    <SectionCard label={t("overview.nextSteps")}>
      <div className="grid grid-cols-3 gap-4">
        {nextSteps.slice(0, 3).map((step) => (
          <StepCard key={step.id} step={step} />
        ))}
      </div>
    </SectionCard>
  );
}
